import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import devicesActions from '@state/device/actions';
import useVariables from '@common/hooks/useVariables';

import alarmsFieldsInfo from '@utils/dictionaries/alarmsFieldsInfo';

function useSimpleCondition({ groupIndex, index }) {

  const { t } = useTranslation();

  const optionDescription = {
    1: t('form.condition_rules.AZURE_ONLINE'),
    2: t('form.condition_rules.AZURE_COM'),
    3: t('form.condition_rules.AZURE_ENABLE'),
    4: t('form.condition_rules.SD_OK'),
    5: t('form.condition_rules.MODBUS_COM'),
    6: t('form.condition_rules.UPDATED_TIME'),
    7: t('form.condition_rules.EXTRTC_OK'),
    8: t('form.condition_rules.UPDATING'),
    9: t('form.condition_rules.UPDATE_OK'),
    10: t('form.condition_rules.HAS_CONN_STR'),
    11: t('form.condition_rules.INTERNET_CONNECTION'),
    12: t('form.condition_rules.RESET_EVENT'),
    13: t('form.condition_rules.RESERVED'),
  };
  // const optionDescription = {
  //   AZURE_ONLINE: t('form.condition_rules.AZURE_ONLINE'),
  //   AZURE_COM: t('form.condition_rules.AZURE_COM'),
  //   AZURE_ENABLE: t('form.condition_rules.AZURE_ENABLE'),
  //   SD_OK: t('form.condition_rules.SD_OK'),
  //   MODBUS_COM: t('form.condition_rules.MODBUS_COM'),
  //   UPDATED_TIME: t('form.condition_rules.UPDATED_TIME'),
  //   EXTRTC_OK: t('form.condition_rules.EXTRTC_OK'),
  //   UPDATING: t('form.condition_rules.UPDATING'),
  //   UPDATE_OK: t('form.condition_rules.UPDATE_OK'),
  //   HAS_CONN_STR: t('form.condition_rules.HAS_CONN_STR'),
  //   INTERNET_CONNECTION: t('form.condition_rules.INTERNET_CONNECTION'),
  //   RESET_EVENT: t('form.condition_rules.RESET_EVENT'),
  //   RESERVED: t('form.condition_rules.RESERVED'),
  // };

  const valueType = [
    {
      name: 'AZURE_ONLINE', value: 1, description: "It's true when the device has sent data to Azure IoT Hub", label: 'INFO',
    },
    {
      name: 'AZURE_COM', value: 2, description: "It's true when the device has conection with Azure IoT Hub", label: 'WARNING',
    },
    {
      name: 'AZURE_ENABLE', value: 3, description: "It's true when the IoT function has all necesary preconditions to be enable", label: 'ERROR',
    },
    {
      name: 'SD_OK', value: 4, description: "It's true when the device has init and use thefile system over SD_CARD", label: 'ERROR',
    },
    {
      name: 'MODBUS_COM', value: 5, description: "It's true when the device does not have any communication problem reading modbus registers", label: 'ERROR',
    },
    {
      name: 'UPDATED_TIME', value: 6, description: "It's true when the year of date is greater than 2019", label: 'WARNING',
    },
    {
      name: 'EXTRTC_OK', value: 7, description: "It's true when the external RTC is initialized and configured without problems", label: 'ERROR',
    },
    {
      name: 'UPDATING', value: 8, description: "It's true when the device is updating the firmware", label: 'INFO',
    },
    {
      name: 'UPDATE_OK', value: 9, description: "It's true if the result of ota update finished successfully", label: 'ERROR',
    },
    {
      name: 'HAS_CONN_STR', value: 10, description: "It's true if the device has a valid connection string", label: 'ERROR',
    },
    {
      name: 'INTERNET_CONNECTION', value: 11, description: "It's true when the device has internet connection", label: 'INFO',
    },
    {
      name: 'RESET_EVENT', value: 12, description: "It's false for the first log sent after reset", label: 'WARNING',
    },
    {
      name: 'RESERVED', value: 13, description: 'Reserved for system(always 0)', label: '',
    },
  ];

  const dataType = [
    {
      name: 'System Work Mode',
      edge_name: 'SysM',
    },
    {
      name: 'Priority',
      edge_name: 'Pri',
    },
    {
      name: 'Battery type',
      edge_name: 'BtTy',
    },
    {
      name: 'Inverter run state',
      edge_name: 'SPHS',
    },
    {
      name: 'Derating Mode',
      edge_name: 'DerM',
    },
  ];
  const sysm = [
    { edge_name: 0, name: 'Waiting module' },
    { edge_name: 1, name: 'Self-test mode' },
    { edge_name: 2, name: 'Reserved' },
    { edge_name: 3, name: 'SysFault module' },
    { edge_name: 4, name: 'Flash module' },
    { edge_name: 5, name: 'PVBAT Online module' },
    { edge_name: 6, name: 'BAT Online module' },
    { edge_name: 7, name: 'PV Offline mode module' },
    { edge_name: 8, name: 'Bat Offline mode module' },
  ];
  const pri = [
    { edge_name: 0, name: 'Load first' },
    { edge_name: 1, name: 'Battery first' },
    { edge_name: 2, name: 'Grid first' },
  ];
  const btty = [
    { edge_name: 0, name: 'Lead - acid' },
    { edge_name: 1, name: 'Lithium battery' },
  ];
  const sphs = [
    { edge_name: 0, name: 'Waiting' },
    { edge_name: 1, name: 'Normal' },
    { edge_name: 3, name: 'Fault' },
  ];
  const derm = [
    { edge_name: 0, name: 'No derate' },
    { edge_name: 1, name: 'PV' },
    { edge_name: 2, name: '*' },
    { edge_name: 3, name: 'VAC' },
    { edge_name: 4, name: 'FAC' },
    { edge_name: 5, name: 'T Boost' },
    { edge_name: 6, name: 'Tinv' },
    { edge_name: 7, name: 'Contol' },
    { edge_name: 8, name: '*' },
    { edge_name: 9, name: 'Over Back by time' },
  ];
  const dispatch = useDispatch();
  const { alarmsFieldsLoading, alarmsFields } = useVariables();

  const isEditDefault = useSelector(({ device }) => device.isEditDefault);
  const orCondition = useSelector(
    ({ device: a }) => a.createEditValues.conditions[groupIndex][index],
  );

  const removeOrCondition = useCallback(() => {

    dispatch(devicesActions.removeOrCondition(groupIndex, index));
    // eslint-disable-next-line
  
  }, []);

  const onChangeOrCondition = useCallback(
    (name, value) => {

      if (name !== 'type' && name !== 'field') {

        const newParams = (function () {

          const params = { ...orCondition.values, [name]: value };
          const array = [value];
          if (name === 'params') {

            delete params.value;
            delete params.values;

          }
          return array;
          // return params;

        }());

        dispatch(
          devicesActions.changeOrCondition(groupIndex, index, {
            ...orCondition,
            params: { values: newParams },
          }),
        );
        return;

      }

      dispatch(
        devicesActions.changeOrCondition(groupIndex, index, {
          ...orCondition,
          [name]: value,
          // values: {},
        }),
      );

    },
    // eslint-disable-next-line
    [orCondition]
  );

  const mapAlarmsFields = useMemo(() => {

    const { results: fields } = alarmsFields;
    if (!fields) return null;

    const mapFields = fields.map((item) => ({
      ...item,
      inputNumberProps: alarmsFieldsInfo[item.edge_name] || null,
    }));

    return mapFields;

    // eslint-disable-next-line
  
  }, [alarmsFields]);

  const filterFields = useMemo(() => {

    if (!mapAlarmsFields) return [];

    const { field } = orCondition;
    const availablesFields = [];

    switch (field) {

      // case 'numeric_threshold':
      //   availablesFields =
      // ['pv1', 'pc1', 'pw1', 'pv2', 'pc2', 'pw2', 'pv3', 'pc3', 'pw3', 'pED', 'pET', 'pPT'];
      //   break;
      // case 'equality':
      //   availablesFields = ['dev', 'mod', 'lic', 'sta'];
      //   break;
      case 'SysM':
        return sysm;
      case 'Pri':
        return pri;
      case 'BtTy':
        return btty;
      case 'SPHS':
        return sphs;
      case 'DerM':
        return derm;
      default:
        break;

    }

    return mapAlarmsFields.filter(({ edge_name }) => availablesFields.includes(edge_name));

  }, [orCondition, mapAlarmsFields]);

  // const fieldSelected = useMemo(() => {

  //   const {
  //     values: field,
  //     // values: { field },
  //   } = orCondition || {};

  //   if (!field || !mapAlarmsFields) return null;
  //   return mapAlarmsFields.find(({ edge_name }) => edge_name === field);
  //   // eslint-disable-next-line

  // }, [orCondition.values ? orCondition.values.field : {}, mapAlarmsFields]);

  return {
    orCondition,
    removeOrCondition,
    onChangeOrCondition,
    alarmsFieldsLoading,
    filterFields,
    valueType,
    // fieldSelected,
    isEditDefault,
    dataType,
    optionDescription,
  };

}

export default useSimpleCondition;
